import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

// PR-UX-08 atom · single responsibility: render a clickable action.
// PR-UX-10 pointing: min touch target 32x32 (desktop) / 44x44 (mobile, future).
// Variants map to existing CSS classes for visual continuity.

type Variant = 'primary' | 'secondary' | 'vermilion' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  block?: boolean;
}

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  vermilion: 'btn-vermilion',
  ghost: 'border border-transparent text-ink-2 hover:text-ink hover:bg-paper',
};

const SIZE_CLASS: Record<Size, string> = {
  sm: 'px-3 py-2 text-[13px]',
  md: '',
  lg: 'btn-large',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    block = false,
    className = '',
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`btn ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${block ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {leadingIcon && <span aria-hidden>{leadingIcon}</span>}
      {loading ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : children}
      {trailingIcon && <span aria-hidden>{trailingIcon}</span>}
    </button>
  );
});

export default Button;
